import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Camera, Save, Eye, EyeOff, User } from "lucide-react";

export default function UserProfilePage() {
  const { token, user, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [profileData, setProfileData] = useState({
    full_name: "",
    telephone: "",
    email: ""
  });
  
  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password: "",
    confirm_password: ""
  });

  const [profilePhoto, setProfilePhoto] = useState(null);

  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || "",
        telephone: user.telephone || "",
        email: user.email || ""
      });
      setProfilePhoto(user.profile_photo);
    }
  }, [user]);

  const handleProfileChange = (field, value) => {
    setProfileData({ ...profileData, [field]: value });
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData({ ...passwordData, [field]: value });
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("photo", file);

      const response = await axios.post(`${API}/profile/photo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      setProfilePhoto(response.data.profile_photo);
      updateUser({ ...user, profile_photo: response.data.profile_photo });
      toast.success("Profile photo updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!profileData.full_name || !profileData.telephone) {
      toast.error("Full name and telephone are required");
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      updateUser({ ...user, ...profileData });
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!passwordData.old_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error("Please fill in all password fields");
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { old_password: passwordData.old_password, new_password: passwordData.new_password },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Password changed successfully");
      setPasswordData({ old_password: "", new_password: "", confirm_password: "" });
      setShowPasswordSection(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const getPhotoUrl = () => {
    if (profilePhoto) {
      // Handle both relative and absolute URLs
      if (profilePhoto.startsWith("http")) {
        return profilePhoto;
      }
      return `${process.env.REACT_APP_BACKEND_URL}${profilePhoto}`;
    }
    return null;
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="user-profile-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-primary">User Profile</h1>
            <p className="text-text-muted mt-1">Manage your account settings and profile photo</p>
          </div>

          {/* Profile Photo Section */}
          <Card className="border-stone-200 mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Profile Photo</CardTitle>
              <CardDescription>Click on the photo to upload a new one</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div 
                  onClick={handlePhotoClick}
                  className="relative w-24 h-24 rounded-full bg-stone-100 border-2 border-dashed border-stone-300 hover:border-primary cursor-pointer transition-colors overflow-hidden group"
                  data-testid="profile-photo-upload"
                >
                  {getPhotoUrl() ? (
                    <img 
                      src={getPhotoUrl()} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-10 h-10 text-stone-400" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="w-6 h-6 text-white" />
                  </div>
                  {uploadingPhoto && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoUpload}
                  data-testid="photo-file-input"
                />
                <div>
                  <p className="font-medium">{user?.full_name}</p>
                  <p className="text-sm text-text-muted">@{user?.username}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={handlePhotoClick}
                    disabled={uploadingPhoto}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {uploadingPhoto ? "Uploading..." : "Change Photo"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profile Information */}
          <Card className="border-stone-200 mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Personal Information</CardTitle>
              <CardDescription>Update your personal details</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    value={user?.username || ""}
                    disabled
                    className="h-12 bg-stone-100 border-stone-200"
                  />
                  <p className="text-xs text-text-muted">Username cannot be changed</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name (Surname First) *</Label>
                  <Input
                    id="full_name"
                    data-testid="profile-fullname-input"
                    placeholder="e.g., Okonkwo John"
                    value={profileData.full_name}
                    onChange={(e) => handleProfileChange("full_name", e.target.value)}
                    className="h-12 bg-stone-50 border-stone-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telephone">Telephone *</Label>
                  <Input
                    id="telephone"
                    data-testid="profile-telephone-input"
                    placeholder="+234 801 234 5678"
                    value={profileData.telephone}
                    onChange={(e) => handleProfileChange("telephone", e.target.value)}
                    className="h-12 bg-stone-50 border-stone-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email (Optional)</Label>
                  <Input
                    id="email"
                    data-testid="profile-email-input"
                    type="email"
                    placeholder="your.email@example.com"
                    value={profileData.email}
                    onChange={(e) => handleProfileChange("email", e.target.value)}
                    className="h-12 bg-stone-50 border-stone-200"
                  />
                </div>

                <Button
                  type="submit"
                  className="rounded-full bg-primary hover:bg-primary-800"
                  disabled={loading}
                  data-testid="save-profile-btn"
                >
                  {loading ? "Saving..." : "Save Changes"}
                  <Save className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Change Password Section */}
          <Card className="border-stone-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Change Password</CardTitle>
                  <CardDescription>Update your account password</CardDescription>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPasswordSection(!showPasswordSection)}
                  data-testid="toggle-password-section-btn"
                >
                  {showPasswordSection ? "Cancel" : "Change Password"}
                </Button>
              </div>
            </CardHeader>
            {showPasswordSection && (
              <CardContent>
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="old_password">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="old_password"
                        data-testid="current-password-input"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter current password"
                        value={passwordData.old_password}
                        onChange={(e) => handlePasswordChange("old_password", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new_password">New Password</Label>
                    <Input
                      id="new_password"
                      data-testid="new-password-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password (min. 6 characters)"
                      value={passwordData.new_password}
                      onChange={(e) => handlePasswordChange("new_password", e.target.value)}
                      className="h-12 bg-stone-50 border-stone-200"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <Input
                      id="confirm_password"
                      data-testid="confirm-password-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                      value={passwordData.confirm_password}
                      onChange={(e) => handlePasswordChange("confirm_password", e.target.value)}
                      className="h-12 bg-stone-50 border-stone-200"
                    />
                  </div>

                  <div className="bg-stone-50 rounded-xl p-4">
                    <p className="text-sm text-text-muted">
                      <strong>Forgot your password?</strong> Please contact the administrator to reset your password.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="rounded-full bg-primary hover:bg-primary-800"
                    disabled={loading}
                    data-testid="change-password-btn"
                  >
                    {loading ? "Changing..." : "Change Password"}
                  </Button>
                </form>
              </CardContent>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
